import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    IconButton,
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Snackbar,
    Alert,
    TablePagination,
    Slide,
    AppBar,
    Toolbar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { Notyf } from 'notyf';
import 'notyf/notyf.min.css'; // Don't forget to import the CSS

// Initialize Notyf
const notyf = new Notyf({
    position: { x: 'left', y: 'bottom' },
    duration: 3000,
});

// Dialog transition
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MaterialTable = () => {
    // State declarations
    const [user, setUser] = useState({});
    const navigate = useNavigate();
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [selectedMaterialId, setSelectedMaterialId] = useState(null);
    const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
    const [currentMaterial, setCurrentMaterial] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Effect to check for auth token and fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/"); // Redirect to login if no token
                return;
            }
            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/"); // Redirect on authentication failure
                }
            }
        };
        fetchProfile();
    }, [navigate]);

    // Effect to fetch materials once the user ID is available
    useEffect(() => {
        if (user.id) {
            fetchMaterials();
        }
    }, [user.id]); // Dependency on user.id ensures this runs after profile is fetched

    // Function to fetch materials from the API
    const fetchMaterials = () => {
        setLoading(true);
        axios.get('https://admin.bizstor.lk/public/api/get-material')
            .then(response => {
                // Assuming the API returns an object with a 'materials' array
                const allMaterials = response.data.materials || [];
                if (user.id) {
                    // Filter materials based on the logged-in user's ID
                    const userMaterials = allMaterials.filter(material =>
                        material.usertoken && material.usertoken.toString().toLowerCase() === user.id.toString().toLowerCase()
                    );
                    setMaterials(userMaterials);
                    setFilteredData(userMaterials);
                }
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching material data:', error);
                setLoading(false);
                notyf.error('Could not fetch materials.');
            });
    };

    // Handler for triggering the delete confirmation dialog
    const handleDelete = (materialId) => {
        setSelectedMaterialId(materialId);
        setOpenConfirmation(true);
    };

    // Function to confirm and execute deletion
    const confirmDelete = () => {
        setLoading(true);
        axios.delete(`https://admin.bizstor.lk/public/api/delete-material/${selectedMaterialId}`)
            .then(() => {
                // Refetch materials to get the updated list
                fetchMaterials();
                notyf.success('Material deleted successfully!');
            })
            .catch(error => {
                console.error('Error deleting material:', error);
                notyf.error('Failed to delete material.');
            })
            .finally(() => {
                setLoading(false);
                setOpenConfirmation(false);
            });
    };

    // Handlers for table pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handler to open the update dialog with the selected material's data
    const handleUpdateClick = (material) => {
        setCurrentMaterial(material);
        setOpenUpdateDialog(true);
    };

    // Handler for form input changes in the update dialog
    const handleUpdateChange = (e) => {
        setCurrentMaterial({
            ...currentMaterial,
            [e.target.name]: e.target.value
        });
    };

    // Handler for submitting the updated material data
    const handleUpdateSubmit = () => {
        setLoading(true);

        const formData = new FormData();
        // Append fields to form data. Note: Laravel expects POST for updates with form-data
        formData.append('material', currentMaterial.material);
        formData.append('price', currentMaterial.price);
        formData.append('code', currentMaterial.code);
        // Add any other fields you need to update
        // formData.append('Bprice', currentMaterial.Bprice);

        axios.post(`https://admin.bizstor.lk/public/api/update-material/${currentMaterial.id}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
            .then(() => {
                setOpenUpdateDialog(false);
                notyf.success('Material updated successfully!');
                // Refetch materials to show the updated data
                fetchMaterials();
            })
            .catch(error => {
                console.error('Error updating material:', error);
                notyf.error('Failed to update material.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // Handler for the search input field
    const handleSearchChange = (event) => {
        const value = event.target.value.toLowerCase();
        setSearchTerm(value);
        const filtered = materials.filter(material => {
            return (
                material?.id?.toString().includes(value) ||
                material?.code?.toString().toLowerCase().includes(value) ||
                material?.material?.toLowerCase().includes(value)
            );
        });
        setFilteredData(filtered);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: {sm:'95%',xs:'90%'},
                maxWidth: 'auto',
                margin: 'auto',
                padding: '10px',
                bgcolor: 'background.paper',
                border: '1px solid #EDF2F7',
                borderRadius: '16px',
                marginTop: '30px',
                position: 'relative'
            }}
        >

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 2 }}>
                <TextField
                    variant="outlined"
                    onChange={handleSearchChange}
                    value={searchTerm}
                    size="small"
                    placeholder="Search by Code, or Material..."
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: '300px',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        },
                    }}
                />
            </Box>

            {loading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'white',
                    zIndex: 9999
                }}>
                    <l-trio
                        size="50"
                        speed="1.3"
                        color="red"
                    ></l-trio>
                </Box>
            )}

            {!loading && (
                <>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', borderBottom: '1px solid #e0e0e0', borderRadius: '8px' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Material Code</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Material Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Price for 1 Unit</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(material => (
                                    <TableRow key={material.id} hover>
                                        <TableCell>{material.code}</TableCell>
                                        <TableCell>{material.material}</TableCell>
                                        <TableCell>Rs.{material.price}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <IconButton onClick={() => handleUpdateClick(material)} aria-label="edit" color="primary">
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(material.id)} aria-label="delete" color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        display="flex"
                        justifyContent="center"
                        width="100%"
                        mt={2}
                    >
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 15]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>
                </>
            )}

            {/* Update Material Dialog */}
            <Dialog
                open={openUpdateDialog}
                onClose={() => setOpenUpdateDialog(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <AppBar sx={{ position: 'relative', bgcolor: '#FAFAFA', boxShadow: 'none', borderBottom: '1px solid #e0e0e0' }}>
                    <Toolbar>
                        <Typography sx={{ flex: 1, color: 'black', fontWeight: 'bold' }} variant="h6">
                            Edit Material
                        </Typography>
                        <IconButton edge="end" color="inherit" onClick={() => setOpenUpdateDialog(false)} aria-label="close">
                            <CloseIcon sx={{ color: 'red' }} />
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogContent sx={{ p: 3 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        name="material"
                        size='small'
                        label="Material Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={currentMaterial.material || ''}
                        onChange={handleUpdateChange}
                    />
                    <TextField
                        margin="dense"
                        name="price"
                        size='small'
                        label="Price for 1 Unit"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={currentMaterial.price || ''}
                        onChange={handleUpdateChange}
                        sx={{ mt: 2 }}
                    />
                    <TextField
                        margin="dense"
                        name="code"
                        size='small'
                        label="Material Code"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={currentMaterial.code || ''}
                        onChange={handleUpdateChange}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenUpdateDialog(false)}>Cancel</Button>
                    <Button variant='contained' onClick={handleUpdateSubmit}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openConfirmation}
                onClose={() => setOpenConfirmation(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this material?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmation(false)}>No</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Yes, Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MaterialTable;
